import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { buildNeedsAttentionList, type AssessmentRow, type ActiveStudentInput, type AttentionStudent } from '@/lib/instructorDashboardAggregation'

export interface DashboardInvitation {
  id: string
  invited_email: string | null
  // Effective status: 'pending' that has passed expires_at is shown as 'expired'
  status: 'pending' | 'accepted' | 'revoked' | 'expired'
  expires_at: string
  accepted_at: string | null
  created_at: string
}

export interface DashboardLesson {
  id: string
  scheduledAt: string
  // null exactly for a lesson started live (start_lesson(), migration 075) but not yet
  // ended — duration is only known once end_lesson() closes it out.
  durationMinutes: number | null
  status: 'scheduled' | 'completed' | 'cancelled'
  studentId: string
  studentName: string | null
}

export interface InstructorDashboardData {
  activeStudentCount: number
  pendingInviteCount: number
  recentInvitations: DashboardInvitation[]
  recentLessons: DashboardLesson[]
  lessonsLoggedLast30Days: number
  needsAttention: AttentionStudent[]
}

interface UseInstructorDashboardReturn {
  data: InstructorDashboardData | null
  loading: boolean
  error: string | null
  refresh: () => void
}

/**
 * The instructor's own dashboard. Every query here relies entirely on existing RLS —
 * instructor_student_relationships/driving_lessons/lesson_skill_assessments' own
 * "own select" policies (instructor_id = auth.uid()) — never a client-supplied filter as
 * the authorization boundary. There is no "selected school" concept for the instructor
 * portal (SelectedSchoolContext is provided only for role='school_admin', see AppShell) —
 * an instructor's dashboard always aggregates across every school/private context THEY
 * work in, which is exactly what auth.uid()-scoped RLS already guarantees without any
 * school-id filter here. Mirrors useStudents.ts's own established pattern (separate
 * queries + client-side merge by id, not embedded joins) for consistency.
 */
export function useInstructorDashboard(): UseInstructorDashboardReturn {
  const { user } = useAuth()
  const [data, setData]     = useState<InstructorDashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError]   = useState<string | null>(null)

  const fetchDashboard = useCallback(async () => {
    if (!user) return
    setLoading(true)
    setError(null)

    try {
      // 1. Active relationships -> student ids (for the count, and as the base list for
      //    the "needing attention" G1 check below). RLS "relationships: own read"
      //    restricts to instructor_id = auth.uid() OR student_id = auth.uid(); since the
      //    caller is an instructor, every returned row is theirs.
      const { data: relRows, error: relErr } = await supabase
        .from('instructor_student_relationships')
        .select('id, student_id')
        .eq('status', 'active')

      if (relErr) {
        setError('Could not load student data. Please try again.')
        return
      }

      const activeStudentIds = (relRows ?? []).map(r => r.student_id)

      // 2. Profiles + student_profiles for the active students only (needed for names on
      //    the attention list and the G1 expiry check). RLS "profiles: instructor read
      //    students" / "student_profiles: instructor read" (migration 060) allow this.
      const profileMap = new Map<string, { full_name: string | null; email: string }>()
      const g1Map = new Map<string, string | null>()
      if (activeStudentIds.length > 0) {
        const { data: profileRows, error: profErr } = await supabase
          .from('profiles')
          .select('id, full_name, email')
          .in('id', activeStudentIds)
        if (profErr) { setError('Could not load student profiles.'); return }
        for (const p of profileRows ?? []) profileMap.set(p.id, { full_name: p.full_name, email: p.email })

        const { data: spRows, error: spErr } = await supabase
          .from('student_profiles')
          .select('user_id, g1_expiry_date')
          .in('user_id', activeStudentIds)
        if (spErr) { setError('Could not load student details.'); return }
        for (const sp of spRows ?? []) g1Map.set(sp.user_id, sp.g1_expiry_date)
      }

      // 3. Recent lessons (any status, across every student — former students' lessons
      //    remain readable per migration 061, but this list only needs recent activity so
      //    the natural ordering surfaces current ones first in practice).
      const { data: lessonRows, error: lessonErr } = await supabase
        .from('driving_lessons')
        .select('id, scheduled_at, duration_minutes, status, student_id')
        .order('scheduled_at', { ascending: false })
        .limit(5)

      if (lessonErr) {
        setError('Could not load lesson data. Please try again.')
        return
      }

      // Lesson student names: batch-fetch any student_id not already covered by the
      // active-students profile map above (a recent lesson may belong to a now-ended
      // relationship) — one extra query at most, never per-row.
      const lessonStudentIds = Array.from(new Set((lessonRows ?? []).map(l => l.student_id)))
      const missingIds = lessonStudentIds.filter(id => !profileMap.has(id))
      if (missingIds.length > 0) {
        const { data: extraProfiles } = await supabase
          .from('profiles')
          .select('id, full_name, email')
          .in('id', missingIds)
        for (const p of extraProfiles ?? []) profileMap.set(p.id, { full_name: p.full_name, email: p.email })
      }

      const recentLessons: DashboardLesson[] = (lessonRows ?? []).map(l => ({
        id: l.id,
        scheduledAt: l.scheduled_at,
        durationMinutes: l.duration_minutes,
        status: l.status,
        studentId: l.student_id,
        studentName: profileMap.get(l.student_id)?.full_name ?? null,
      }))

      // 4. Simple, non-invented progress stat: a plain count of lessons actually logged
      //    in the last 30 days — no score, no percentage, just an existing persisted count.
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()
      const { count: lessonsLoggedLast30Days, error: countErr } = await supabase
        .from('driving_lessons')
        .select('id', { count: 'exact', head: true })
        .gte('scheduled_at', thirtyDaysAgo)

      if (countErr) {
        setError('Could not load lesson activity. Please try again.')
        return
      }

      // 5. All of this instructor's skill assessments (RLS already scopes to
      //    instructor_id = auth.uid()) — deduplicated to the latest per (student, skill)
      //    by the pure helper, exactly mirroring useStudentSkillProgress's own definition
      //    of "currently needs practice" so the two can never silently disagree.
      const { data: assessmentRowsRaw, error: assessErr } = await supabase
        .from('lesson_skill_assessments')
        .select('student_id, skill_id, outcome, updated_at')

      if (assessErr) {
        setError('Could not load assessment data. Please try again.')
        return
      }

      const assessmentRows: AssessmentRow[] = (assessmentRowsRaw ?? []).map(a => ({
        studentId: a.student_id,
        skillId: a.skill_id,
        outcome: a.outcome,
        updatedAt: a.updated_at,
      }))

      const activeStudentInputs: ActiveStudentInput[] = activeStudentIds.map(id => ({
        studentId: id,
        fullName: profileMap.get(id)?.full_name ?? null,
        email: profileMap.get(id)?.email ?? '',
        g1ExpiryDate: g1Map.get(id) ?? null,
      }))

      const needsAttention = buildNeedsAttentionList(activeStudentInputs, assessmentRows, new Date())

      // 6. Recent invitations (last 5).
      //    RLS "invitations: instructor manage own" USING (instructor_id = auth.uid())
      //    — returns only this instructor's invitations. Never exposes token_hash.
      const { data: invRows, error: invErr } = await supabase
        .from('invitations')
        .select('id, invited_email, status, expires_at, accepted_at, created_at')
        .order('created_at', { ascending: false })
        .limit(5)

      if (invErr) {
        setError('Could not load invitation data. Please try again.')
        return
      }

      // Compute effective status: the DB does not auto-expire 'pending' rows,
      // so resolve client-side the same way Enroll.tsx does.
      const now = new Date()
      const resolvedInvitations: DashboardInvitation[] = (invRows ?? []).map(inv => ({
        id:           inv.id,
        invited_email: inv.invited_email,
        expires_at:   inv.expires_at,
        accepted_at:  inv.accepted_at,
        created_at:   inv.created_at,
        status: (
          inv.status === 'pending' && new Date(inv.expires_at) < now
            ? 'expired'
            : inv.status
        ) as DashboardInvitation['status'],
      }))

      const pendingInviteCount = resolvedInvitations.filter(i => i.status === 'pending').length

      setData({
        activeStudentCount: activeStudentIds.length,
        pendingInviteCount,
        recentInvitations: resolvedInvitations,
        recentLessons,
        lessonsLoggedLast30Days: lessonsLoggedLast30Days ?? 0,
        needsAttention,
      })
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => { fetchDashboard() }, [fetchDashboard])

  return { data, loading, error, refresh: fetchDashboard }
}
